import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import {
  Contingent,
  StoreContingentSchema,
} from '../../../shared/models/catalog/contingent.model';
import { ContingentService } from '../../../shared/services/catalog/contingent.service';
import { applyZodErrors } from '../../../shared/util/zod-form-errors';

interface BlockingCategory {
  id: number;
  title: string;
}

@Component({
  selector: 'app-contingents-page',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    CardModule,
    TableModule,
    ButtonModule,
    DialogModule,
    InputTextModule,
    InputNumberModule,
    TextareaModule,
    IconFieldModule,
    InputIconModule,
    ToastModule,
    ConfirmDialogModule,
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './contingents-page.component.html',
  styleUrl: './contingents-page.component.scss',
})
export class ContingentsPageComponent implements OnInit {
  contingentService = inject(ContingentService);
  #messageService = inject(MessageService);
  #confirmationService = inject(ConfirmationService);
  #translate = inject(TranslateService);
  #fb = inject(FormBuilder);

  dialogVisible = signal(false);
  editing = signal<Contingent | null>(null);
  submitting = signal(false);

  form: FormGroup = this.#fb.group({
    title: ['', [Validators.required, Validators.maxLength(255)]],
    description: [null as string | null],
    total_amount: [0 as number, [Validators.required, Validators.min(0)]],
    instruction_amount: [0 as number, [Validators.required, Validators.min(0)]],
  });

  ngOnInit(): void {
    this.contingentService.getContingents();
  }

  openCreateDialog() {
    this.editing.set(null);
    this.form.reset({
      title: '',
      description: null,
      total_amount: 0,
      instruction_amount: 0,
    });
    this.dialogVisible.set(true);
  }

  openEditDialog(contingent: Contingent) {
    this.editing.set(contingent);
    this.form.reset({
      title: contingent.title,
      description: contingent.description ?? null,
      total_amount: contingent.total_amount,
      instruction_amount: contingent.instruction_amount,
    });
    this.dialogVisible.set(true);
  }

  closeDialog() {
    this.dialogVisible.set(false);
    this.editing.set(null);
  }

  onSubmit() {
    if (this.submitting()) return;
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }

    const raw = this.form.getRawValue();
    const parsed = StoreContingentSchema.safeParse({
      title: raw.title,
      description: raw.description,
      total_amount: raw.total_amount,
      instruction_amount: raw.instruction_amount,
    });
    if (!parsed.success) {
      applyZodErrors(parsed.error, this.form);
      return;
    }

    const editing = this.editing();
    this.submitting.set(true);

    const call$ = editing
      ? this.contingentService.update(editing.id, parsed.data)
      : this.contingentService.create(parsed.data);

    call$.subscribe({
      next: () => {
        this.submitting.set(false);
        this.dialogVisible.set(false);
        this.editing.set(null);
        this.contingentService.getContingents();
        this.#messageService.add({
          severity: 'success',
          summary: this.#translate.instant('label.successHeader'),
          detail: this.#translate.instant(
            editing
              ? 'catalog.contingents.updated'
              : 'catalog.contingents.created',
          ),
        });
      },
      error: (e: unknown) => {
        this.submitting.set(false);
        this.#applyServerErrors(e);
        this.#messageService.add({
          severity: 'error',
          summary: this.#translate.instant('label.errorHeader'),
          detail: this.#translate.instant(
            editing
              ? 'catalog.contingents.updateError'
              : 'catalog.contingents.createError',
          ),
        });
      },
    });
  }

  onRowDelete(event: Event, contingent: Contingent) {
    this.#confirmationService.confirm({
      target: event.target as EventTarget,
      header: this.#translate.instant(
        'catalog.contingents.deleteConfirmHeader',
      ),
      message: this.#translate.instant(
        'catalog.contingents.deleteConfirmMessage',
      ),
      acceptLabel: this.#translate.instant('label.delete'),
      rejectLabel: this.#translate.instant('label.cancel'),
      acceptButtonProps: { severity: 'danger' },
      accept: () => this.#performDelete(contingent.id),
    });
  }

  #performDelete(id: number) {
    this.contingentService.delete(id).subscribe({
      next: () => {
        this.contingentService.getContingents();
        this.#messageService.add({
          severity: 'success',
          summary: this.#translate.instant('label.successHeader'),
          detail: this.#translate.instant('catalog.contingents.deleted'),
        });
      },
      error: (e: unknown) => {
        const status = (e as { status?: number })?.status;
        const blocking = (
          e as { error?: { blocking_categories?: BlockingCategory[] } }
        )?.error?.blocking_categories;
        if (status === 409 && Array.isArray(blocking) && blocking.length > 0) {
          const titles = blocking.map((c) => c.title).join(', ');
          this.#messageService.add({
            severity: 'warn',
            summary: this.#translate.instant(
              'catalog.contingents.blockedByCategoriesHeader',
            ),
            detail: this.#translate.instant(
              'catalog.contingents.blockedByCategoriesMessage',
              { categories: titles },
            ),
            life: 8000,
          });
          return;
        }
        this.#messageService.add({
          severity: 'error',
          summary: this.#translate.instant('label.errorHeader'),
          detail: this.#translate.instant('catalog.contingents.deleteError'),
        });
      },
    });
  }

  #applyServerErrors(error: unknown) {
    const errBag = (error as { error?: { errors?: Record<string, string[]> } })
      ?.error?.errors;
    if (!errBag) return;
    for (const key of Object.keys(errBag)) {
      const control = this.form.get(key);
      if (control) {
        control.setErrors({ server: { message: errBag[key][0] } });
        control.markAsTouched();
      }
    }
  }
}
